import React, { useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import BaseIntegrationIcon from "@reusables/BaseIntegrationIcon";
import UIButton from "@reusables/UIButton";
import {
    useSyncTwentyFourSevenOfficeCustomersMutation,
    useSyncTwentyFourSevenOfficeSuppliersMutation,
} from "@redux/features/integrations/integrationsApi";
import { performImport } from "@redux/features/dfs/thunks";
import { selectIsAnyOperationActive, selectOperation } from "@redux/features/dfs/dfsSlice";
import { isDataTransferStateActive, isDataTransferStateFinished } from "@redux/features/dfs/types";
import { useAbility } from "@/casl.config";
import { isSystemIntegratedSelector } from "@redux/features/auth/authSlice";
import { useAppDispatch, useAppSelector } from "@redux/hooks";

type TwentyFourSevenOfficeRecordSyncEntity = "customers" | "suppliers";

/**
 * `progressIndex` points into the `current` / `total` arrays of the data transfer progress updates,
 * which the Finago import reports as [customers, suppliers, products, categories, sale orders].
 */
const entityConfig = {
    customers: { importEntity: "customer", progressIndex: 0, permissionModule: "customer" },
    suppliers: { importEntity: "supplier", progressIndex: 1, permissionModule: "supplier" },
} as const;

interface TwentyFourSevenOfficeRecordSyncButtonProps {
    entity: TwentyFourSevenOfficeRecordSyncEntity;
}

export default function TwentyFourSevenOfficeRecordSyncButton({
    entity,
}: TwentyFourSevenOfficeRecordSyncButtonProps) {
    const { t } = useTranslation("", {
        keyPrefix: "imports.modules.twenty_four_seven_office.recordSync",
    });

    const ability = useAbility();
    const dispatch = useAppDispatch();
    const isTwentyFourSevenOfficeIntegrated = useAppSelector(
        isSystemIntegratedSelector("twenty_four_seven_office")
    );
    const importState = useAppSelector(selectOperation("import"));
    const isAnyTransferActive = useAppSelector(selectIsAnyOperationActive);

    const [syncCustomers] = useSyncTwentyFourSevenOfficeCustomersMutation();
    const [syncSuppliers] = useSyncTwentyFourSevenOfficeSuppliersMutation();

    const { importEntity, progressIndex, permissionModule } = entityConfig[entity];
    const entityLabel = t(`entities.${entity}`) as string;

    // The sync runs in the background as a regular Finago import, so we recognize it by the entities this client registered when starting it
    const isOwnSync =
        importState?.module === "twenty_four_seven_office" &&
        !!importState.entities?.includes(importEntity);
    const activeOwnSync =
        isOwnSync && isDataTransferStateActive(importState) ? importState : undefined;
    const isSyncing = !!activeOwnSync;

    const progress = activeOwnSync?.progress?.[progressIndex];
    const progressPercent =
        progress && progress.total > 0
            ? Math.round((progress.current / progress.total) * 100)
            : undefined;

    // The result arrives through the sockets, so we notify once this sync's state flips from active to finished
    const wasSyncingRef = useRef(isSyncing);

    useEffect(() => {
        if (wasSyncingRef.current && isOwnSync && isDataTransferStateFinished(importState)) {
            if (importState.has_errors) {
                toast.error(t("toasts.error", { entity: entityLabel }));
            } else if (importState.errors) {
                toast.warn(t("toasts.warning", { entity: entityLabel, count: importState.errors }));
            } else {
                toast.success(t("toasts.success", { entity: entityLabel }));
            }
        }

        wasSyncingRef.current = isSyncing;
    }, [importState]);

    if (!isTwentyFourSevenOfficeIntegrated || !ability.can("create", permissionModule)) {
        return null;
    }

    const handleSync = () => {
        const sync = entity === "customers" ? syncCustomers : syncSuppliers;

        const operation = dispatch(
            performImport("twenty_four_seven_office", () => sync().unwrap(), {
                entities: [importEntity],
            })
        );

        // performImport already reports and logs an immediate API failure (e.g., another transfer is running, Finago is not connected).
        void operation.catch(() => undefined);
    };

    const label = (() => {
        if (!isSyncing) {
            return t("buttons.sync");
        }

        if (progressPercent === undefined) {
            return t("buttons.syncing");
        }

        return t("buttons.syncingWithProgress", { percent: progressPercent });
    })();

    return (
        <UIButton
            type="button"
            variant="secondary"
            size="md"
            loading={isSyncing}
            disabled={isAnyTransferActive}
            aria-label={t("aria.sync", { entity: entityLabel })}
            aria-busy={isSyncing}
            onClick={handleSync}
        >
            <BaseIntegrationIcon integration="twenty_four_seven_office" />
            <span>{label}</span>
        </UIButton>
    );
}
